import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import FloatingAIChat from './components/FloatingAIChat';
import InstallPWA from './components/InstallPWA';
import UpdateBanner from './components/UpdateBanner';
import { getBackendUrl, isNativePlatform, getStoredToken, setStoredToken, clearStoredToken } from './lib/platform';
import { initRevenueCat } from './lib/revenuecat';
import { initPushNotifications } from './lib/native';
import { nativePost, nativeGet } from './lib/nativeApi';
import './App.css';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import CalculatorPage from './pages/CalculatorPage';
import MDBPage from './pages/MDBPage';
import CommunityPage from './pages/CommunityPage';
import MessagesPage from './pages/MessagesPage';
import PricingPage from './pages/PricingPage';
import SubscriptionPage from './pages/SubscriptionPage';
import ProfilePage from './pages/ProfilePage';
import AIAgentPage from './pages/AIAgentPage';
import ClassroomPage from './pages/ClassroomPage';
import DirectoryPage from './pages/DirectoryPage';
import ProsDirectoryPage from './pages/ProsDirectoryPage';
import IntegrationsPage from './pages/IntegrationsPage';
import CGUPage from './pages/CGUPage';
import CGVPage from './pages/CGVPage';
import ChartePage from './pages/ChartePage';
import ConfidentialitePage from './pages/ConfidentialitePage';
import ContactPage from './pages/ContactPage';
import FAQPage from './pages/FAQPage';
import PropertiesPage from './pages/PropertiesPage';
import AIStatsPage from './pages/AIStatsPage';
import CadastreAgentPage from './pages/CadastreAgentPage';
import BiensVendusAgentPage from './pages/BiensVendusAgentPage';
import EventsPage from './pages/EventsPage';
import DiagnosticClimatiquePage from './pages/DiagnosticClimatiquePage';
import AdminPropertiesPage from './pages/AdminPropertiesPage';
import AdminAnalyticsPage from './pages/AdminAnalyticsPage';
import OpenClawGuidePage from './pages/OpenClawGuidePage';
import LaunchGuestsPage from './pages/LaunchGuestsPage';
import BlogPage from './pages/BlogPage';
import BlogArticlePage from './pages/BlogArticlePage';
import BlogWritePage from './pages/BlogWritePage';
import AdminBlogPage from './pages/AdminBlogPage';
import AdminInstallmentsPage from './pages/AdminInstallmentsPage';
import AdminTinypagesPage from './pages/AdminTinypagesPage';
import PortfolioPage from './pages/PortfolioPage';
import SharedAnalysisPage from './pages/SharedAnalysisPage';
import AlertsPage from './pages/AlertsPage';
import { usePageViewTracking } from './hooks/useAnalytics';


// Dynamic API URL: absolute on native (Capacitor), env-based on web
const BACKEND_URL = getBackendUrl();
export const API = `${BACKEND_URL}/api`;

// Axios interceptor: ALWAYS attach Bearer token + disable cookies on mobile
axios.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // On mobile, don't send cookies (CapacitorHttp handles networking natively)
  if (isNativePlatform()) {
    config.withCredentials = false;
  }
  return config;
});

// Response interceptor: log errors with full detail for mobile debugging
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isNativePlatform()) {
      const info = {
        url: error.config?.url,
        status: error.response?.status,
        message: error.message,
      };
      console.error('[Mobile API Error]', JSON.stringify(info));
    }
    return Promise.reject(error);
  }
);

// ScrollToTop component - scrolls to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = getStoredToken();

    // Try native API first (uses CapacitorHttp on iOS = NSURLSession)
    if (isNativePlatform() || (typeof window !== 'undefined' && window.location?.hostname === 'app.emergentagent.com')) {
      if (!token) { setLoading(false); return; }
      try {
        const userData = await nativeGet('/auth/me', token);
        if (userData) {
          setUser(userData);
          initRevenueCat(userData.email || userData._id);
          initPushNotifications();
        } else {
          setUser(null);
          clearStoredToken();
        }
      } catch {
        setUser(null);
        clearStoredToken();
      }
      setLoading(false);
      return;
    }

    // Web: use axios with cookies
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      clearStoredToken();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    // Use nativePost (CapacitorHttp on iOS, fetch on web fallback)
    if (isNativePlatform() || (typeof window !== 'undefined' && window.location?.hostname === 'app.emergentagent.com')) {
      const data = await nativePost('/auth/login', { email, password });
      if (data.access_token) setStoredToken(data.access_token);
      setUser(data.user);
      initRevenueCat();
      return data;
    }

    // Web: use axios with cookies
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    if (response.data.access_token) setStoredToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name, status, profession) => {
    const body = { email, password, name, status: status || 'particulier', profession: profession || null };

    if (isNativePlatform() || (typeof window !== 'undefined' && window.location?.hostname === 'app.emergentagent.com')) {
      const data = await nativePost('/auth/register', body);
      if (data.access_token) setStoredToken(data.access_token);
      setUser(data.user);
      initRevenueCat();
      return data;
    }

    const response = await axios.post(`${API}/auth/register`, body, { withCredentials: true });
    if (response.data.access_token) setStoredToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      if (isNativePlatform()) {
        const token = getStoredToken();
        await nativePost('/auth/logout', {}, token);
      } else {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      }
    } catch (e) {}
    clearStoredToken();
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// OAuth Callback Handler
const AuthCallback = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const hasProcessed = React.useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
          updateUser(response.data);
          navigate('/dashboard', { replace: true, state: { user: response.data } });
        } catch (error) {
          console.error('OAuth callback error:', error);
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processCallback();
  }, [navigate, updateUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0D47A1] mx-auto mb-4"></div>
        <p className="text-slate-600">Connexion en cours...</p>
      </div>
    </div>
  );
};

// Block route on native mobile (Apple Guideline 3.1.1 - no pricing/payment pages)
const WebOnlyRoute = ({ children }) => {
  if (isNativePlatform()) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0D47A1]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const ClubRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0D47A1]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const tier = user?.subscription_tier;
  if (tier !== 'club' && tier !== 'expert' && tier !== 'founder') {
    return <Navigate to="/dashboard?upgrade=true" replace />;
  }

  return children;
};

const AnalyticsTracker = () => {
  const { user, loading } = useAuth();
  usePageViewTracking(user, loading);
  return null;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for OAuth callback BEFORE normal routing
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/pricing" element={<WebOnlyRoute><PricingPage /></WebOnlyRoute>} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calculator"
        element={
          <ProtectedRoute>
            <CalculatorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mdb"
        element={
          <ProtectedRoute>
            <MDBPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/community"
        element={
          <ProtectedRoute>
            <CommunityPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-agent"
        element={
          <ProtectedRoute>
            <AIAgentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/classroom"
        element={
          <ProtectedRoute>
            <ClassroomPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/classroom/:courseId"
        element={
          <ProtectedRoute>
            <ClassroomPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/directory"
        element={
          <ProtectedRoute>
            <DirectoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pros-directory"
        element={
          <ProtectedRoute>
            <ProsDirectoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute>
            <IntegrationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/properties"
        element={
          <ProtectedRoute>
            <PropertiesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-stats"
        element={
          <ProtectedRoute>
            <AIStatsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portfolio"
        element={
          <ProtectedRoute>
            <PortfolioPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/alerts"
        element={
          <ProtectedRoute>
            <AlertsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cadastre"
        element={
          <ClubRoute>
            <CadastreAgentPage />
          </ClubRoute>
        }
      />
      <Route
        path="/biens-vendus"
        element={
          <ClubRoute>
            <BiensVendusAgentPage />
          </ClubRoute>
        }
      />
      <Route
        path="/risques-naturels"
        element={
          <ClubRoute>
            <DiagnosticClimatiquePage />
          </ClubRoute>
        }
      />
      <Route
        path="/diagnostic-climatique"
        element={
          <ClubRoute>
            <DiagnosticClimatiquePage />
          </ClubRoute>
        }
      />
      <Route
        path="/events"
        element={
          <ProtectedRoute>
            <EventsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/properties"
        element={
          <ProtectedRoute>
            <AdminPropertiesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute>
            <AdminAnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/guide-openclaw"
        element={
          <ProtectedRoute requiredTier="club">
            <OpenClawGuidePage />
          </ProtectedRoute>
        }
      />
      <Route path="/cgu" element={<CGUPage />} />
      <Route path="/cgv" element={<WebOnlyRoute><CGVPage /></WebOnlyRoute>} />
      <Route path="/charte" element={<ChartePage />} />
      <Route path="/confidentialite" element={<ConfidentialitePage />} />
      <Route path="/privacy" element={<ConfidentialitePage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/shared/:shareToken" element={<SharedAnalysisPage />} />
      <Route
        path="/launch-guests"
        element={
          <ProtectedRoute>
            <LaunchGuestsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/faq" element={<FAQPage />} />
      <Route
        path="/abonnement"
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        }
      />
      {/* Blog - public */}
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogArticlePage />} />
      {/* Blog - auth required */}
      <Route
        path="/blog/write"
        element={
          <ProtectedRoute>
            <BlogWritePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/blog"
        element={
          <ProtectedRoute>
            <AdminBlogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/installments"
        element={
          <ProtectedRoute>
            <AdminInstallmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tinypages"
        element={
          <ProtectedRoute>
            <AdminTinypagesPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <AnalyticsTracker />
        <AppRouter />
        <FloatingAIChat />
        <UpdateBanner apiUrl={API} />
        <InstallPWA />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
